import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../store';
import { fetchAnalytics, selectAnalytics, selectAnalyticsLoading, selectAnalyticsError } from '../../store/slices/analyticsSlice';
import OverviewTab from '../dashboard/OverviewTab';
import FuelTypesTab from '../dashboard/FuelTypesTab';
import LocationsTab from '../dashboard/LocationsTab';
import UsagePatternsTab from '../dashboard/UsagePatternsTab';
import AnalyticsKPIs from '../dashboard/AnalyticsKPIs';
import { RefreshCw, AlertCircle } from 'lucide-react';
import '../dashboard/DashboardStyles.css';
import './AnalyticsPage.css';

const AnalyticsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const analyticsData = useAppSelector(selectAnalytics);
  const isLoading = useAppSelector(selectAnalyticsLoading);
  const error = useAppSelector(selectAnalyticsError);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    dispatch(fetchAnalytics({}));
  }, [dispatch]);

  const renderTabContent = () => {
    if (isLoading && !analyticsData) {
      return (
        <div className="analytics-loading">
          <div className="spinner" />
          <p>Loading analytics data...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="analytics-error">
          <AlertCircle size={24} />
          <p>{error}</p>
          <button onClick={() => dispatch(fetchAnalytics({}))}>Retry</button>
        </div>
      );
    }

    switch (activeTab) {
      case 'overview':
        return <OverviewTab data={analyticsData?.transactionSuccessRate} />;
      case 'fuel-types':
        return <FuelTypesTab data={analyticsData?.fuelTypeDistribution} />;
      case 'locations':
        return <LocationsTab data={analyticsData?.keyLocations} />;
      case 'usage-patterns':
        return <UsagePatternsTab data={analyticsData?.topCompaniesBySpending} />;
      default:
        return <OverviewTab data={analyticsData?.transactionSuccessRate} />;
    }
  };

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <div className="header-info">
          <h1>Analytics Dashboard</h1>
          <p>Comprehensive insights into fuel card usage patterns and trends.</p>
        </div>
        <button
          className="refresh-btn"
          onClick={() => dispatch(fetchAnalytics({}))}
          disabled={isLoading}
        >
          <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
          Refresh Data
        </button>
      </div>

      <AnalyticsKPIs data={analyticsData?.kpis} isLoading={isLoading} />

      <div className="dashboard-tabs">
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Usage Overview
          </button>
          <button
            className={`tab-button ${activeTab === 'fuel-types' ? 'active' : ''}`}
            onClick={() => setActiveTab('fuel-types')}
          >
            Fuel Distribution
          </button>
          <button
            className={`tab-button ${activeTab === 'locations' ? 'active' : ''}`}
            onClick={() => setActiveTab('locations')}
          >
            Daily Patterns
          </button>
          <button
            className={`tab-button ${activeTab === 'usage-patterns' ? 'active' : ''}`}
            onClick={() => setActiveTab('usage-patterns')}
          >
            Trends & Analysis
          </button>
        </div>

        <div className="analytics-content">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
